import type { ConversationListItem } from '@chatbot/shared-types';
import type { WsStatus } from '../../hooks/WebSocketProvider.tsx';
import { EmptyState } from '../EmptyState.tsx';
import { Skeleton } from '../Skeleton.tsx';
import { BotonSilencio } from './BotonSilencio.tsx';
import { ConversationRow } from './ConversationRow.tsx';
import type { ConversationStateFilter } from './useConversationsList.ts';

export const STATE_FILTERS: Array<{ label: string; value: ConversationStateFilter }> = [
  { label: 'Todas', value: 'all' },
  { label: 'Esperando atención', value: 'pending_human' },
  { label: 'En atención', value: 'human_active' },
  { label: 'Resueltas', value: 'resolved' },
  { label: 'Con bot', value: 'bot_active' },
];

interface Props {
  items: ConversationListItem[];
  loading: boolean;
  error: string | null;
  wsStatus: WsStatus;
  stateFilter: ConversationStateFilter;
  search: string;
  tenantId: string;
  tenantIds: string[];
  selectedId: string | null;
  className?: string;
  onStateFilterChange: (value: ConversationStateFilter) => void;
  onSearchChange: (value: string) => void;
  onTenantChange: (tenantId: string) => void;
  onSelect: (item: ConversationListItem) => void;
  onRetry: () => void;
}

/** Panel izquierdo del workspace: siempre visible, nunca se pierde al abrir un chat. */
export function ConversationListPanel({
  items,
  loading,
  error,
  wsStatus,
  stateFilter,
  search,
  tenantId,
  tenantIds,
  selectedId,
  className = '',
  onStateFilterChange,
  onSearchChange,
  onTenantChange,
  onSelect,
  onRetry,
}: Props) {
  return (
    <section
      aria-label="Lista de conversaciones"
      className={`flex-col border-r border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 min-h-0 ${className}`}
    >
      <header className="px-4 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-h5 font-bold text-neutral-900 dark:text-neutral-100">
            Conversaciones
          </h1>
          <div className="flex items-center gap-2">
            <BotonSilencio />
            <WsIndicator status={wsStatus} />
          </div>
        </div>

        {tenantIds.length > 1 ? (
          <select
            value={tenantId}
            onChange={(e) => onTenantChange(e.target.value)}
            aria-label="Tenant"
            className="select-native mt-2 w-full py-1 px-2 border border-neutral-300 dark:border-neutral-700 rounded bg-white dark:bg-neutral-800 text-sm focus:outline-none"
          >
            {tenantIds.map((tid) => (
              <option key={tid} value={tid}>
                {tid}
              </option>
            ))}
          </select>
        ) : (
          <p className="mt-1 text-caption text-neutral-500 dark:text-neutral-400 truncate">
            {tenantId ? `Tenant: ${tenantId}` : 'Selecciona un tenant'}
          </p>
        )}

        <input
          type="search"
          placeholder="Buscar por usuario o texto…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="input input-sm mt-2 w-full"
          aria-label="Buscar conversaciones"
        />

        <div className="mt-2 flex gap-1.5 flex-wrap">
          {STATE_FILTERS.map((f) => {
            const isActive = stateFilter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => onStateFilterChange(f.value)}
                aria-pressed={isActive}
                className={`px-2.5 py-1 rounded-full text-caption font-semibold transition ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading && items.length === 0 ? (
          <div className="p-4">
            <Skeleton rows={6} />
          </div>
        ) : error ? (
          <div className="p-6 text-center flex flex-col items-center gap-3">
            <p className="text-sm text-danger-600 font-semibold">
              No se pudieron cargar las conversaciones: {error}
            </p>
            <button type="button" onClick={onRetry} className="btn btn-primary btn-sm">
              Reintentar
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
            <EmptyState
              message={
                search
                  ? 'Ninguna conversación coincide con la búsqueda.'
                  : stateFilter === 'pending_human'
                    ? 'No hay conversaciones esperando atención.'
                    : 'Aún no hay conversaciones registradas.'
              }
            />
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {items.map((c) => (
              <ConversationRow
                key={`${c.tenantId}-${c.conversationId}`}
                item={c}
                active={c.conversationId === selectedId}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function WsIndicator({ status }: { status: WsStatus }) {
  if (status === 'degraded') {
    return (
      <span className="text-caption px-2 py-0.5 bg-warning-50 border border-warning-200 dark:bg-warning-950/20 dark:border-warning-900/40 text-warning-700 dark:text-warning-300 rounded font-medium">
        Modo degradado · Actualizando cada 5s
      </span>
    );
  }
  if (status === 'open') {
    return (
      <span className="text-caption px-2 py-0.5 bg-success-50 border border-success-100 dark:bg-success-900/20 dark:border-success-900/40 text-success-700 dark:text-success-500 rounded font-medium flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-success-600 dark:bg-success-500" />
        En vivo
      </span>
    );
  }
  if (status === 'connecting') {
    return (
      <span className="text-caption px-2 py-0.5 bg-neutral-100 border border-neutral-200 dark:bg-neutral-800 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 rounded font-medium flex items-center gap-1.5">
        <span className="spinner spinner-xs" />
        Conectando…
      </span>
    );
  }
  return null;
}
